import React from 'react';
import { Container, Title, Paper, Box, Switch, Checkbox, MultiSelect, Stack, Group, Collapse, Tooltip, Loader, LoadingOverlay } from '@mantine/core';
import { usePIIConfiguration, useUpdatePIIConfiguration } from '../../../hooks/useSettings';
import { notifications } from '@mantine/notifications';
import { IconMail } from '@tabler/icons';

const DEFAULT_INFO_TYPES = [
  { name: 'EMAIL_ADDRESS' },
  { name: 'US_SOCIAL_SECURITY_NUMBER' },
  { name: 'CREDIT_CARD_NUMBER' },
];

const PIIRedactionSettings = () => {
  const { data: piiData, isLoading } = usePIIConfiguration();
  const { mutate: updatePII, isPending: isUpdating } = useUpdatePIIConfiguration();

  // Track loading state for each field
  const [loadingFields, setLoadingFields] = React.useState<Record<string, boolean>>({});

  const isEnabled = piiData?.config?.isEnabled ?? false;
  const selectedTypes = piiData?.config?.selectedTypes ?? [];
  const excludedDomains = piiData?.config?.excludedDomains ?? [];

  const handleUpdate = async (update: Partial<{
    isEnabled: boolean;
    selectedTypes: string[];
    excludedDomains: string[];
  }>, fieldId?: string) => {
    if (fieldId) {
      setLoadingFields(prev => ({ ...prev, [fieldId]: true }));
    }

    updatePII(
      {
        isEnabled: update.isEnabled ?? isEnabled,
        selectedTypes: update.selectedTypes ?? selectedTypes,
        excludedDomains: update.excludedDomains ?? excludedDomains,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Success',
            message: 'PII settings updated successfully',
            color: 'green',
          });
          if (fieldId) {
            setLoadingFields(prev => ({ ...prev, [fieldId]: false }));
          }
        },
        onError: (error) => {
          notifications.show({
            title: 'Error',
            message: 'Failed to update PII settings',
            color: 'red',
          });
          if (fieldId) {
            setLoadingFields(prev => ({ ...prev, [fieldId]: false }));
          }
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Box m="md">
        <Title order={2} mb="xl">Text PII Redaction</Title>
        <Paper p="md">
          <Stack spacing="xl">
            <Checkbox checked={false} label="Loading..." disabled />
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box m="md">
      <Title order={2} mb="xl">Text PII Redaction</Title>
      <Paper p="md">
        <Stack spacing="xl">
          <Group align="center">
            <Switch
              label="Enable PII Redaction"
              checked={isEnabled}
              onChange={(event) => handleUpdate({ isEnabled: event.currentTarget.checked }, 'main-switch')}
              disabled={loadingFields['main-switch']}
              size="md"
            />
            {loadingFields['main-switch'] && <Loader size="xs" />}
          </Group>

          <Box>
            <Title order={4} mb="sm">Select Entity Types to Redact</Title>
            <Stack spacing="xs">
              {DEFAULT_INFO_TYPES.map((type) => (
                <Box key={type.name}>
                  <Group align="center" spacing="xs">
                    <Checkbox
                      label={type.name.split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')}
                      checked={selectedTypes.includes(type.name)}
                      onChange={(event) => {
                        const newSelectedTypes = event.currentTarget.checked
                          ? [...selectedTypes, type.name]
                          : selectedTypes.filter((t: string) => t !== type.name);
                        handleUpdate({ selectedTypes: newSelectedTypes }, `checkbox-${type.name}`);
                      }}
                      disabled={loadingFields[`checkbox-${type.name}`]}
                    />
                    {loadingFields[`checkbox-${type.name}`] && <Loader size="xs" />}
                  </Group>
                  {type.name === 'EMAIL_ADDRESS' && selectedTypes.includes('EMAIL_ADDRESS') && (
                    <Box ml="xl" mt="xs">
                      <Collapse in={true}>
                        <Tooltip label="Email domains to exclude" position="left" withArrow >
                          <Box>
                            <MultiSelect
                              data={excludedDomains}
                              value={excludedDomains}
                              icon={loadingFields['domains-multiselect'] ? <Loader size="xs" /> : <IconMail size={14} />}
                              placeholder="Enter email domains to exclude (e.g., company.com)"
                              description="Email domains to exclude from redaction"
                              searchable
                              creatable
                              getCreateLabel={(query) => `+ Add ${query}`}
                              onCreate={(query) => {
                                const cleanedDomain = query.replace('@', '').trim();
                                
                                const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
                                
                                if (!domainRegex.test(cleanedDomain)) {
                                  notifications.show({
                                    title: 'Invalid Domain',
                                    message: 'Please enter a valid domain (e.g., company.com)',
                                    color: 'red',
                                  });
                                  return null;
                                }

                                const newDomains = [...excludedDomains, cleanedDomain];
                                handleUpdate({ excludedDomains: newDomains }, 'domains-multiselect');
                                return cleanedDomain;
                              }}
                              onChange={(newDomains) => handleUpdate({ excludedDomains: newDomains }, 'domains-multiselect')}
                              disabled={loadingFields['domains-multiselect']}
                            />
                          </Box>
                        </Tooltip>
                      </Collapse>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PIIRedactionSettings; 